import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getLanguageName, getLanguageOptions } from 'common/i18n/utils';
import { generatePath } from 'common/utils/route';
import { duplicateStory } from 'admin/actions';
import { useAdminDispatch } from 'admin/reducers';
import { STORY_FLOW_PAGE } from 'admin/constants/routes';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import { MODAL } from 'admin/constants/common';
import Text from 'admin/components/common/Text';
import { Hint } from 'admin/components/common/Form/Hint';
import { Icon } from 'admin/components/common/Icon';
import Button from 'admin/components/common/Button';
import { Select } from 'admin/components/common/Form';
import AdminError from 'admin/components/common/ErrorBoundary/AdminError';
import { toast } from 'admin/components/common/MessageContainer/toast';
import t from 'common/utils/translate';
import type { SupportedLanguage } from 'common/i18n/types';
import type { InitialStoryEditorChatMessage } from 'admin/components/pages/Story/StoryEditorChat/use-auto-send-initial-message';

type DuplicateStoryResponse = {
	success: boolean;
	result?: { duplicatedStoryId: string };
};

type DuplicateStoryForTranslationInput = {
	storyId: string;
	language: SupportedLanguage;
	duplicateStory: (storyId: string) => Promise<DuplicateStoryResponse>;
};

type TranslationDestination = {
	storyId: string;
	initialChatMessage: InitialStoryEditorChatMessage;
};

const getTranslationPrompt = (language: SupportedLanguage) => `Translate this story to ${getLanguageName(language)}.`;

export const duplicateStoryForTranslation = async ({
	storyId,
	language,
	duplicateStory: duplicate,
}: DuplicateStoryForTranslationInput): Promise<TranslationDestination> => {
	const response = await duplicate(storyId);
	const duplicatedStoryId = response.result?.duplicatedStoryId;

	if (!response.success) {
		throw new AdminError(t('responseError.badRequest'), false);
	}

	if (!duplicatedStoryId) {
		throw new AdminError(t('responseError.badRequest'));
	}

	return {
		storyId: duplicatedStoryId,
		initialChatMessage: {
			id: `translate-${duplicatedStoryId}`,
			text: getTranslationPrompt(language),
		},
	};
};

type Props = ModalManagerProvidedProps<MODAL.TRANSLATE_STORY>;

const TranslationModal: React.FC<Props> = ({ open, ...props }) => {
	const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage | null>(null);
	const [isBusy, setIsBusy] = useState(false);
	const languageOptions = getLanguageOptions(props.data.language);
	const dispatch = useAdminDispatch();
	const history = useHistory();

	const handleTranslate = async (_selectedLanguage: SupportedLanguage) => {
		setIsBusy(true);

		try {
			const destination = await duplicateStoryForTranslation({
				storyId: props.data.storyId,
				language: _selectedLanguage,
				duplicateStory: storyId => dispatch(duplicateStory(storyId)),
			});

			props.close();
			history.push(generatePath(STORY_FLOW_PAGE, { storyId: destination.storyId }), {
				initialStoryEditorChatMessage: destination.initialChatMessage,
			});
		} catch (error) {
			if (!(error instanceof AdminError)) {
				toast.error((error as Error)?.message || t('responseError.badRequest'));
			}
		} finally {
			setIsBusy(false);
		}
	};

	const handleClose = () => {
		if (!isBusy) {
			setSelectedLanguage(null);
			props.close();
		}
	};

	const handleLanguageChange = (value: SupportedLanguage) => {
		setSelectedLanguage(value);
	};

	if (!props.data.storyId) {
		return null;
	}

	return (
		<Modal
			title={
				<Text
					size="subtitle"
					weight="semibold"
					text={t('translationModal.title', { name: props.data.storyName })}
				/>
			}
			open={open}
			destroyOnHidden
			width={564}
			maskClosable={!isBusy}
			closable={!isBusy}
		>
			<ModalBody>
				<Select
					label={{ children: t('translationModal.select.label') }}
					options={languageOptions}
					placeholder={t('translationModal.select.placeholder')}
					value={selectedLanguage}
					eventListeners={{ onChange: handleLanguageChange }}
					disabled={isBusy}
					isSearchable
				/>
				<Hint
					style={{ marginTop: 8 }}
					text={
						<>
							<Icon type="info" color="var(--ra-color-black-20)" width={14} />
							&nbsp;{t('translationModal.hint')}
						</>
					}
				/>
			</ModalBody>
			<ModalFooter>
				<Button onClick={handleClose} disabled={isBusy} view="secondary">
					{t('translationModal.btn.cancel')}
				</Button>
				<Button
					loading={isBusy}
					disabled={!selectedLanguage || isBusy}
					onClick={selectedLanguage ? () => handleTranslate(selectedLanguage) : undefined}
					view="primary"
				>
					{isBusy ? t('translationModal.status.starting') : t('translationModal.btn.translate')}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default TranslationModal;
